import React, {
    useContext,
    useEffect, useMemo, useState 
} from 'react';
import {
    Box, Slide, 
    Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const Benefits = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksAnswer = useMemo(() => {
        return formContext.answers.totalDrinksBaseline;
    }, [formContext.answers.totalDrinksBaseline]);
    const backJump = useMemo(() => {
        return formContext.answers.totalDrinksBaseline === 0 ? 4 : 1;
    }, [formContext.answers.totalDrinksBaseline]);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        summaryContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            marginTop: '24px',
            alignItems: 'center'
        },
        summary: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            width: '100%'
        },
        item: {
            display: 'grid',
            gridTemplateColumns: '32px 1fr',
            width: '100%',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: '32px 12px 12px 32px'
        },
        itemValue: {
            left: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            backgroundColor: theme.palette.tertiary.main,
            border: `4px solid ${theme.palette.white}`,
            borderRadius: '50%',
            zIndex: 1,
            transform: 'scale(0)',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        itemTextContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: '32px',
            width: '100%',
            minHeight: '46px',
            borderRadius: '12px',
            backgroundColor: theme.palette.tertiary['100'],
            transform: 'translate(-100vw, 0)',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`
        },
        itemText: {
            marginLeft: '28px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`
        },
        summaryText: {
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: '24px',
            textAlign: 'center',
            padding: '0 16px',
            margin: 0,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        fadeIn: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes grow': {
            '0%': {
                transform: 'scale(0)' 
            },
            '100%': {
                transform: 'scale(1)' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                transform: 'translate(-100vw, 0)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);
    });

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            backJump={backJump}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                            >
                                {drinksAnswer === 0 ? 'With Sunnyside, you\'ll feel some incredible benefits:' : 'And you\'ll feel the impact far beyond just saving money:'}
                            </Typography>
                            <Box
                                key={2}
                                className={classes.summaryContainer}
                            >
                                <Box className={classes.summary}>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '24px',
                                                    height: '23.25px' 
                                                }}
                                                width="16"
                                                height="15"
                                                viewBox="0 0 16 15"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M15.9997 5.06135V5.02824C15.9997 2.20865 13.8876 0 11.1896 0C9.93018 0 8.81663 0.503377 7.99987 1.427C7.18365 0.503377 6.06952 0 4.81012 0C2.11352 0 0 2.20883 0 5.03706L0.00202703 5.05058C-0.00135124 5.22692 0.00743235 5.8411 0.237163 6.7215H3.32969L4.27563 5.00531C4.36009 4.85328 4.52833 4.76478 4.70266 4.7837C4.87563 4.80262 5.02089 4.92559 5.06955 5.09316L5.72697 7.34852L6.5898 6.07354C6.68304 5.93503 6.84926 5.86409 7.01344 5.89043C7.17965 5.91611 7.31411 6.03367 7.3614 6.19515L8.08302 8.63766L9.25259 4.604C9.30596 4.42089 9.47217 4.29454 9.66069 4.29117H9.66948C9.85596 4.29117 10.0222 4.41211 10.0796 4.59049L10.7735 6.72148H12.8789C13.1174 6.72148 13.3113 6.9154 13.3113 7.15391C13.3113 7.39444 13.1174 7.58633 12.8789 7.58633H10.4608C10.2723 7.58633 10.1061 7.46674 10.0493 7.28903L9.69323 6.19446L8.50338 10.3004C8.45 10.4856 8.28041 10.6119 8.08853 10.6119H8.0865C7.89596 10.6119 7.72705 10.4856 7.673 10.3024L6.79262 7.31871L5.92777 8.59504C5.83453 8.73355 5.66831 8.80586 5.50211 8.78017C5.33792 8.7545 5.20143 8.63491 5.15414 8.4741L4.53117 6.33637L3.96565 7.36405C3.8893 7.50256 3.74404 7.58703 3.5866 7.58703H0.512922C1.25481 9.57964 3.07305 12.389 7.50264 14.7788C7.65467 14.8599 7.82764 14.9031 8.00061 14.9031C8.17358 14.9031 8.34655 14.8599 8.50059 14.7788C15.8632 10.8039 16.0086 5.67556 15.9997 5.06135Z" />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Higher Energy Levels' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '24px',
                                                    height: '24px' 
                                                }}
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.8 4C8.8 3.55817 8.44183 3.2 8 3.2C7.55817 3.2 7.2 3.55817 7.2 4V8.8C7.2 9.06748 7.33368 9.31727 7.55624 9.46564L9.95624 11.0656C10.3239 11.3107 10.8206 11.2114 11.0656 10.8438C11.3107 10.4761 11.2114 9.97944 10.8438 9.73436L8.8 8.37185V4Z"
                                                />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Increased Productivity' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '24px',
                                                    height: '24px' 
                                                }}
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M9.59508 6.42086C9.59508 6.42086 10.2853 2.36098 7.46892 0.598434C7.42696 1.296 7.23831 1.97687 6.91531 2.59657C6.59231 3.21627 6.14221 3.76086 5.59441 4.19478C4.39162 5.2523 2.12945 7.63013 2.15351 10.1652C2.14487 11.2489 2.43924 12.3135 3.00342 13.2388C3.5676 14.1641 4.37913 14.9134 5.34645 15.402C5.38101 14.9127 5.51289 14.4352 5.73436 13.9975C5.95583 13.5598 6.26244 13.1707 6.63621 12.853C6.95294 12.6094 7.218 12.3052 7.41593 11.9581C7.61386 11.611 7.74069 11.228 7.78904 10.8314C8.61705 11.2716 9.31665 11.9191 9.81939 12.7107C10.3221 13.5023 10.6108 14.4108 10.6572 15.3474V15.3594C11.5702 14.9412 12.3498 14.2786 12.9095 13.4448C13.4693 12.611 13.7875 11.6386 13.8289 10.6353C14.0287 8.25373 12.7242 5.01545 11.5667 3.95699C11.1297 4.93259 10.4511 5.78061 9.59508 6.42086Z" />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Healthier Appearance' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '24px',
                                                    height: '25px' 
                                                }}
                                                width="16"
                                                height="17"
                                                viewBox="0 0 16 17"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M7.60198 16.6667C12.2329 16.6667 16 12.8974 16 8.26378C16 4.23639 13.1327 0.767203 9.18561 0.0109427C8.90368 -0.0430758 8.62774 0.106976 8.51977 0.371066C8.40579 0.629155 8.49577 0.93526 8.73572 1.09732C10.4753 2.29173 11.5191 4.2664 11.5191 6.37913C11.5191 9.90234 8.65174 12.7713 5.12457 12.7713C3.61293 12.7713 2.14327 12.2311 0.991545 11.2528C0.775596 11.0667 0.457671 11.0607 0.229725 11.2348C0.00777775 11.4089 -0.0642052 11.721 0.061765 11.9731C1.48943 14.866 4.38074 16.6667 7.60198 16.6667Z" />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Improved Quality of Sleep' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '24px',
                                                    height: '24px' 
                                                }}
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g clipPath="url(#clip0_2174_129640)">
                                                    <path d="M8.18013 0.58896C8.63082 0.0931917 9.45121 0.47925 9.35645 1.14254L8.69113 5.79981H15.0185C15.6088 5.79981 15.9187 6.50041 15.5216 6.93723L7.81852 15.4107C7.36782 15.9064 6.54744 15.5204 6.64219 14.8571L7.30752 10.1998H0.980167C0.389833 10.1998 0.0799019 9.49921 0.477008 9.06239L8.18013 0.58896Z" />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_2174_129640">
                                                        <rect
                                                            width="16"
                                                            height="16"
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Better Mood' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.item}>
                                        <Box className={classes.itemValue}>
                                            <svg
                                                style={{
                                                    width: '21.18px',
                                                    height: '24px' 
                                                }}
                                                width="15"
                                                height="16"
                                                viewBox="0 0 15 16"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M0.941176 12.234C0.941176 12.2344 0.941176 12.2349 0.941176 12.2353C0.941176 13.4381 1.84828 14.4329 3.0144 14.5717C3.3792 15.4278 4.22362 16 5.17647 16C5.94654 16 6.62927 15.6265 7.05882 15.0528C7.48838 15.6264 8.17111 16 8.94118 16C9.89402 16 10.7384 15.4278 11.1032 14.5717C12.2694 14.4329 13.1765 13.4381 13.1765 12.2353C13.1765 12.2349 13.1765 12.2344 13.1765 12.234C13.7634 11.7928 14.1176 11.0967 14.1176 10.3529C14.1176 9.93976 14.007 9.53261 13.8012 9.17647C14.007 8.82033 14.1176 8.41318 14.1176 8C14.1176 7.22729 13.7489 6.52499 13.1339 6.08772C13.1622 5.94221 13.1765 5.79482 13.1765 5.64706C13.1765 4.90334 12.8222 4.20725 12.2353 3.76602C12.2353 3.76565 12.2353 3.76508 12.2353 3.76471C12.2353 2.99784 11.8579 2.28066 11.2371 1.84245C11.0018 0.777224 10.0527 0 8.94118 0C8.17111 0 7.48838 0.373459 7.05882 0.9472C6.62927 0.373459 5.94654 0 5.17647 0C4.06513 0 3.11586 0.777224 2.88056 1.84245C2.25958 2.28066 1.88235 2.99784 1.88235 3.76471C1.88235 3.76508 1.88235 3.76565 1.88235 3.76602C1.29544 4.20725 0.941176 4.90334 0.941176 5.64706C0.941176 5.79501 0.955294 5.94221 0.983718 6.08772C0.368753 6.52499 0 7.22729 0 8C0 8.41318 0.110682 8.82033 0.316423 9.17647C0.110682 9.53261 0 9.93976 0 10.3529C0 11.0967 0.354259 11.7928 0.941176 12.234ZM7.52941 12.9967L7.91228 12.8053C8.04235 12.7403 8.18786 12.7059 8.33336 12.7059H8.94118C9.20113 12.7059 9.41177 12.4952 9.41177 12.2353C9.41177 11.9753 9.20113 11.7647 8.94118 11.7647H8.33336C8.05647 11.7647 7.77995 11.8289 7.52941 11.9471V8.76725C7.97289 8.57318 8.45722 8.47059 8.94118 8.47059C9.20113 8.47059 9.41177 8.25995 9.41177 8C9.41177 7.74005 9.20113 7.52941 8.94118 7.52941C8.46249 7.52941 7.984 7.60979 7.52941 7.76132V4.38212C7.83849 4.59238 8.20273 4.70588 8.58466 4.70588H9.41177C9.67172 4.70588 9.88235 4.49525 9.88235 4.23529C9.88235 3.97534 9.67172 3.76471 9.41177 3.76471H8.58466C8.33336 3.76471 8.09694 3.66682 7.91906 3.48913L7.52941 3.09929V2.35294C7.52941 1.5744 8.16264 0.941176 8.94118 0.941176C9.54786 0.941176 10.0727 1.32781 10.2686 1.87859L9.41515 1.87275C9.41384 1.87275 9.41289 1.87275 9.41177 1.87275C9.15332 1.87275 8.94306 2.08132 8.94118 2.34014C8.93929 2.60009 9.1488 2.81224 9.40838 2.81393L10.8053 2.82353C10.8066 2.82353 10.8075 2.82353 10.8087 2.82353C10.8459 2.82353 10.8819 2.81807 10.9167 2.80979C11.1543 3.06711 11.2941 3.40612 11.2941 3.76471C11.2941 3.78372 11.2928 3.80461 11.2917 3.82551L10.7586 4.49976C10.5011 4.82541 10.3582 5.232 10.3537 5.64706H8.94118C8.68122 5.64706 8.47059 5.85769 8.47059 6.11765C8.47059 6.3776 8.68122 6.58824 8.94118 6.58824H10.3529V7.05882C10.3529 7.31878 10.5636 7.52941 10.8235 7.52941C11.0835 7.52941 11.2941 7.31878 11.2941 7.05882V5.66701C11.2941 5.456 11.3662 5.24875 11.4968 5.08329L11.8306 4.66108C12.0855 4.9216 12.2353 5.27398 12.2353 5.64687C12.2353 5.81365 12.2046 5.98024 12.1438 6.14193C12.0585 6.36932 12.1604 6.62457 12.3789 6.73054C12.8708 6.9696 13.1765 7.45581 13.1765 8C13.1765 8.16301 13.149 8.32132 13.0959 8.47059H11.2941C11.0342 8.47059 10.8235 8.68122 10.8235 8.94118C10.8235 9.20113 11.0342 9.41177 11.2941 9.41177H12.8262C12.838 9.43209 12.8506 9.45205 12.8659 9.47087C13.0692 9.72424 13.1765 10.0292 13.1765 10.3529C13.1765 10.6716 13.0675 10.9758 12.8768 11.2201C12.5416 10.6995 11.958 10.3529 11.2941 10.3529H10.3529C10.0934 10.3529 9.88235 10.1417 9.88235 9.88235C9.88235 9.6224 9.67172 9.41177 9.41177 9.41177C9.15181 9.41177 8.94118 9.6224 8.94118 9.88235C8.94118 10.6609 9.5744 11.2941 10.3529 11.2941H11.2941C11.8131 11.2941 12.2353 11.7163 12.2353 12.2353C12.2353 12.883 11.7963 13.4289 11.1987 13.5942L11.0496 12.8489C10.9986 12.5939 10.7488 12.4294 10.496 12.4798C10.2411 12.5308 10.0759 12.7787 10.1269 13.0336L10.3151 13.9746C10.3127 13.9825 10.3089 13.9898 10.3068 13.9979C10.1466 14.6225 9.58532 15.0588 8.94118 15.0588C8.85515 15.0588 8.7712 15.0498 8.68932 15.0351L9.27398 14.4504C9.45788 14.2667 9.45788 13.9688 9.27398 13.785C9.09007 13.6011 8.79228 13.6011 8.60857 13.785L7.85092 14.5427C7.65007 14.2987 7.52941 13.9868 7.52941 13.6471V12.9967ZM2.35294 8.47059H1.02174C0.968659 8.32132 0.941176 8.16301 0.941176 8C0.941176 7.45581 1.24687 6.9696 1.73873 6.73073C1.95727 6.62475 2.05911 6.36951 1.97384 6.14212C1.91304 5.98042 1.88235 5.81384 1.88235 5.64706C1.88235 5.30146 2.01242 4.97487 2.23398 4.72113L3.17158 6.29873C3.18287 6.31737 3.19511 6.33525 3.20866 6.35238C3.29807 6.464 3.3984 6.56377 3.50739 6.65073L2.96132 7.1968C2.77741 7.38052 2.77741 7.67849 2.96132 7.86221C3.05336 7.95407 3.17365 8 3.29412 8C3.41459 8 3.53487 7.95407 3.62692 7.86221L4.44555 7.04358C4.52235 7.05299 4.59991 7.05882 4.67859 7.05882H5.17647C5.43642 7.05882 5.64706 6.84819 5.64706 6.58824C5.64706 6.32828 5.43642 6.11765 5.17647 6.11765H4.6784C4.40075 6.11765 4.14136 5.99755 3.96216 5.78711L2.83012 3.88198C2.82635 3.83944 2.82353 3.79991 2.82353 3.76471C2.82353 3.29732 3.05958 2.86268 3.44358 2.60085L4.26014 3.00913C4.32772 3.04301 4.39944 3.05901 4.47021 3.05901C4.64282 3.05901 4.80904 2.96376 4.89148 2.79887C5.00762 2.5664 4.91332 2.28367 4.68104 2.16753L3.89139 1.77261C4.11407 1.27887 4.60951 0.941176 5.17647 0.941176C5.95501 0.941176 6.58824 1.5744 6.58824 2.35294V4.08847C6.27915 3.87821 5.91492 3.76471 5.53299 3.76471H5.17647C4.91652 3.76471 4.70588 3.97534 4.70588 4.23529C4.70588 4.49525 4.91652 4.70588 5.17647 4.70588H5.53299C5.78428 4.70588 6.02071 4.80376 6.19859 4.98146L6.58824 5.37129V8.16282C6.19294 8.28104 5.76038 8.2496 5.38692 8.06287C5.15407 7.94616 4.87191 8.04066 4.75558 8.27332C4.63944 8.50579 4.73374 8.78852 4.96602 8.90466C5.32668 9.08499 5.72216 9.17515 6.11765 9.17515C6.27539 9.17515 6.43275 9.15671 6.58824 9.12809V10.8235H5.57835C5.21958 10.8235 4.89713 10.6242 4.73656 10.3032L4.42089 9.67191C4.30456 9.43925 4.02202 9.34532 3.78955 9.46127C3.55727 9.5776 3.46296 9.86014 3.57911 10.0926L3.89478 10.724C4.21572 11.366 4.86061 11.7647 5.57835 11.7647H6.58824V13.6471C6.58824 14.2603 6.19445 14.7821 5.64706 14.9766V14.2319C5.64706 13.7291 5.45129 13.2565 5.09572 12.9009L4.56791 12.3733C4.384 12.1896 4.08583 12.1896 3.90249 12.3733C3.71859 12.5572 3.71859 12.855 3.90249 13.0389L4.43012 13.5665C4.60781 13.7442 4.70588 13.9806 4.70588 14.2321V14.9777C4.27275 14.8241 3.93016 14.4636 3.81082 13.9981C3.75736 13.7894 3.56932 13.6444 3.35511 13.6444C3.34946 13.6444 3.34362 13.6444 3.33798 13.6446L3.29412 13.6471C2.51558 13.6471 1.88235 13.0138 1.88235 12.2353C1.88235 12.2221 1.88348 12.2072 1.88405 12.1931L2.68574 11.3914C2.86965 11.2077 2.86965 10.9097 2.68574 10.726C2.50184 10.5421 2.20405 10.5421 2.02033 10.726L1.37675 11.3694C1.10287 11.1063 0.941176 10.7407 0.941176 10.3529C0.941176 10.0292 1.04847 9.72424 1.25176 9.47087C1.26682 9.45205 1.27962 9.43209 1.29148 9.41177H2.35294C2.61289 9.41177 2.82353 9.20113 2.82353 8.94118C2.82353 8.68122 2.61289 8.47059 2.35294 8.47059Z" />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Reduced Brain Fog' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                <Typography
                                    variant={TypographyVariants.H5}
                                    className={classes.fadeIn}
                                >
                                    And the list goes on!
                                </Typography>
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default Benefits;
